import {util} from 'fabric';

// Fabric v5/v6 → v7 type name mapping
const TYPE_MAP = {
    'text': 'Text',
    'i-text': 'IText',
    'textbox': 'Textbox',
    'group': 'Group',
    'circle': 'Circle',
    'rect': 'Rect',
    'path': 'Path',
    'image': 'Image',
    'line': 'Line',
    'polygon': 'Polygon',
    'polyline': 'Polyline',
    'triangle': 'Triangle',
    'ellipse': 'Ellipse',
    'activeSelection': 'ActiveSelection',
    'activeselection': 'ActiveSelection',
};

export const migrate4to5 = (data, element) => {
    if (element?.custom?.type === 'player') {
        for (const obj of element.getObjects()) {
            if (obj.type === 'circle') {
                obj.startAngle = util.radiansToDegrees(obj.startAngle);
                obj.endAngle = util.radiansToDegrees(obj.endAngle);
            }
        }
    }
};

/**
 * Deep clean an object by converting to JSON and back
 * This removes any undefined values that might exist
 */
const deepClean = (obj) => {
    return JSON.parse(JSON.stringify(obj, (key, value) => {
        // Replace undefined with null during stringification
        return value === undefined ? null : value;
    }));
};

/**
 * Remove all null and undefined properties from an object
 * Fabric.js v6 is stricter and doesn't handle undefined properties well
 */
const removeUndefinedProps = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    
    Object.keys(obj).forEach(key => {
        if (obj[key] === undefined || obj[key] === null) {
            delete obj[key];
        } else if (Array.isArray(obj[key])) {
            // Process array elements
            obj[key].forEach(item => {
                if (typeof item === 'object' && item !== null) {
                    removeUndefinedProps(item);
                }
            });
        } else if (typeof obj[key] === 'object') {
            removeUndefinedProps(obj[key]);
        }
    });
};

/**
 * Normalize type names from Fabric v5 to v7
 * v7 uses specific type names like 'Text', 'IText', 'Group', etc.
 */
const normalizeTypeName = (obj) => {
    if (!obj || !obj.type) return;
    if (TYPE_MAP[obj.type]) {
        obj.type = TYPE_MAP[obj.type];
    }
};

/**
 * Recursively ensure all text objects have a valid text property
 * This is required for Fabric.js v6 compatibility
 */
const ensureTextProperty = (obj, depth = 0) => {
    if (!obj) return;
    
    // First normalize the type name
    normalizeTypeName(obj);
    
    // Handle all text-like objects - ensure text property exists
    const isTextObject = obj.type === 'Textbox' || obj.type === 'Text' || obj.type === 'IText';
    
    if (isTextObject) {
        // CRITICAL: Ensure text property exists and is a string
        if (obj.text === undefined || obj.text === null) {
            obj.text = '';
        }

        // Ensure it's a string
        if (typeof obj.text !== 'string') {
            obj.text = String(obj.text);
        }

        // Also ensure other required text properties exist
        if (!obj.fontSize) obj.fontSize = 40;
        if (!obj.fontFamily) obj.fontFamily = 'Arial';
        
        // Fabric.js v7: defaults are center/center, ensure explicit
        if (!obj.originX) obj.originX = 'center';
        if (!obj.originY) obj.originY = 'center';
    }
    
    // Recursively handle nested objects in groups - THIS IS CRITICAL
    // Fabric.js v6 deserializes nested objects internally, so we MUST fix them before enlivenObjects is called
    if (obj.objects && Array.isArray(obj.objects)) {
        console.log(`[migrate5to6] Found group with ${obj.objects.length} nested objects`);

        for (let i = 0; i < obj.objects.length; i++) {
            const nestedObj = obj.objects[i];
            console.log(`[migrate5to6] Nested object ${i}:`, nestedObj.type);
            
            // Normalize nested object type
            normalizeTypeName(nestedObj);
            
            // Fix Circle endAngle if it's insanely large (v5 bug)
            if (nestedObj.type === 'Circle' && nestedObj.endAngle && nestedObj.endAngle > 360) {
                console.log(`[migrate5to6] Fixing Circle endAngle from ${nestedObj.endAngle} to 360`);
                nestedObj.endAngle = 360;
            }
            
            // Fix nested text objects ONLY - don't try to fix circles or other shapes
            if (nestedObj.type === 'Textbox' || nestedObj.type === 'Text' || nestedObj.type === 'IText') {
                console.log(`[migrate5to6] Fixing nested text object ${i}, text=`, nestedObj.text);

                if (nestedObj.text === undefined || nestedObj.text === null) {
                    console.log(`[migrate5to6] Setting empty text for nested object ${i}`);
                    nestedObj.text = '';
                } else if (typeof nestedObj.text !== 'string') {
                    console.log(`[migrate5to6] Converting text to string for nested object ${i}`);
                    nestedObj.text = String(nestedObj.text);
                }

                console.log(`[migrate5to6] Nested object ${i} text is now:`, nestedObj.text);
            }
            
            // Recursively process deeper nesting
            ensureTextProperty(nestedObj, depth + 1);
        }
    }
};

/**
 * Migrate object types from v5/v6 to v7 format (recursive)
 */
export const migrate6to7 = (data) => {
    if (!data) return;
    normalizeTypeName(data);
    if (data.objects && Array.isArray(data.objects)) {
        data.objects.forEach(migrate6to7);
    }
};

export const migrate5to6 = (data) => {
    if (!data) return;
    
    // First, deep clean to remove any undefined values
    const cleaned = deepClean(data);
    
    // Copy cleaned properties back to original object
    Object.keys(cleaned).forEach(key => {
        data[key] = cleaned[key];
    });
    
    // Remove any remaining undefined/null properties
    removeUndefinedProps(data);
    
    // Fabric.js v6 migration: Ensure all text objects have required properties
    ensureTextProperty(data);
};